import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Phone, Snowflake, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const carouselImages = [
    { src: "/images/hero/hero-vrf-outdoor.png", alt: "VRF Outdoor Units" },
    { src: "/images/hero/carousel-1.jpg", alt: "Mini Split AC System" },
    { src: "/images/hero/carousel-2.webp", alt: "VRF System Installation" },
    { src: "/images/hero/carousel-3.jpg", alt: "Commercial AC Installation" },
  ];

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 bg-gradient-to-br from-white via-amber-50/50 to-white overflow-hidden" data-testid="hero-section">
      {/* Decorative depth */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-[28rem] h-[28rem] bg-yellow-300/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_1px_1px,#000_1px,transparent_0)] [background-size:26px_26px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="order-2 lg:order-1 lg:col-span-2 animate-fade-in-up">
            <div className="mb-4" data-testid="hero-brand">
              <span className="block text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-wide">
                MARCO <span className="text-amber-600">AIR CONDITION</span>
              </span>
              <span className="block text-base text-amber-600 font-semibold italic mt-1">Experience Comfort Cooling</span>
            </div>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-6 animate-fade-in hover:scale-105 transition-transform duration-300" data-testid="hero-badge">
              <Snowflake className="h-4 w-4 mr-2 animate-pulse" />
              Trusted HVAC Expert Services Since 1980
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-fade-in-up" data-testid="hero-title">
              Expert <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-yellow-500 animate-gradient">HVAC Solutions</span> for Your Comfort
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed animate-fade-in" data-testid="hero-description">
              Professional Air Conditioning Installation, Service & Repair for Residential, Commercial and Industrial Spaces. Specializing in Air Chiller, VRF, Ductable, Cassette & Split AC Systems.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <a 
                href={getWhatsAppLink("Hi! I would like to get a free consultation for HVAC services.")}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-[#4ADE80] hover:bg-[#25D366] text-black font-semibold text-lg px-8 py-3 rounded-md transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/30"
                data-testid="hero-cta-primary"
              >
                <svg className="h-5 w-5 group-hover:animate-bounce" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Get Free Consultation
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="tel:9825884460"
                className="group inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-black font-semibold text-lg px-8 py-3 rounded-md transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/30"
                data-testid="hero-cta-secondary"
              >
                <Phone className="h-5 w-5 group-hover:animate-pulse" />
                Call Now
              </a>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { number: "500+", label: "Projects Completed" },
                { number: "45+", label: "Years Experience" },
                { number: "24/7", label: "Support Available" },
                { number: "100%", label: "Client Satisfaction" },
              ].map((stat, index) => (
                <div key={index} className="text-center md:text-left group hover:scale-110 transition-transform duration-300 cursor-default">
                  <div className="text-3xl font-bold text-amber-600 group-hover:text-yellow-500 transition-colors">{stat.number}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Image Carousel */}
          <div className="order-1 lg:order-2 lg:col-span-3 relative animate-fade-in">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl ring-1 ring-amber-500/20">
              {/* Carousel Images */}
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-auto flex-shrink-0 object-cover"
                  />
                ))}
              </div>
              
              {/* Carousel Controls */}
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 hover:scale-110"
                aria-label="Next slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Carousel Indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentSlide === index 
                        ? 'bg-amber-500 w-8' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Decorative gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-amber-500/50 rounded-full flex justify-center pt-2">
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
